/**
 * The app catalog (APP-ENTITLEMENT-001): which apps exist, which are anchors, what each attaches to, the
 * capability keys each grants, its pricing model, its size dimensions and its metered consumables.
 *
 * This list is the STRUCTURE of the catalog — the part code gates on — and it seeds `public.app_catalog`, whose
 * seed guard freezes `appKey`, `kind`, `capabilityKeys` and `pricingModel` on every seeded row. The COMMERCIAL
 * facts (prices, trial days, inclusions, caps, suggestions, whether a row is offered) are rows the console edits;
 * none of them is here. A price never appears in code (G-MONEY; `platformPlanCatalogOnePriceSource`).
 *
 * Keys are the cross-app-policy app keys where one exists (`parcel`, `palm`, `patrol`, `payday`, `planner`,
 * `port`, `prep`, `prospect`); `publish`, `pro`, `pronto` and `storefront` are catalog-only keys. `launchAppKey`
 * names the cross-app-policy app that OPENS the row (PublishPuffin and the storefront open PurchasePuffin's tenant
 * admin; ProntoPuffin and ProPuffin open nothing until they have a surface).
 *
 * Owner rulings behind the rows (QUESTIONS.md): three anchors with PublishPuffin the third (Q-AE-25) and ProPuffin
 * "coming soon" (Q-AE-22, Q-AE-29); PalmPuffin and PrepPuffin free with any anchor (Q-AE-14, Q-AE-23); PatrolPuffin
 * a share of recovery (Q-AE-16); at most two size dimensions per app and never a user (Q-AE-3); PayDayPuffin a flat
 * base on the account plus whole dollars per employee paid (Q-AE-14); PortPuffin's AI
 * receptionist the one paid AI line (Q-AE-27) with its outcomes a priced consumable on top of its minutes
 * (Q-AE-57 / Q-AE-58); registers three included per location (Q-AE-20); the auto-blog four
 * posts a month included (Q-AE-28); customer accounts a per-company SETTING — the one SWITCHABLE included app,
 * `accounts`, on with every plan at creation and switched off per company (Q-LS-85 / Q-LS-86 / Q-LS-87).
 *
 * @module @puffin-core/app-catalog/catalog
 */

'use strict';

const {
  APP_KIND,
  PRICING_MODEL,
  DIMENSION_KEY,
  CONSUMABLE_KEY,
  QUANTITY_SOURCE,
  CONSUMABLE_SCOPE,
  PRICE_ENDING,
} = require('./vocabulary');

/** The two size dimensions an app may carry at most (Q-AE-3). */
const MAX_DIMENSIONS_PER_APP = 2;

/** The share of a recovered refund PatrolPuffin earns, in basis points (Q-AE-16: 20 %). */
const PATROL_RECOVERY_SHARE_BPS = 2000;

/**
 * @typedef {Object} AppDimension
 * @property {string} dimensionKey - one of `DIMENSION_KEY_VALUES`.
 * @property {string} quantitySource - `derived` | `declared`.
 * @property {?{dimensionKey: string, quantity: number}} includedPerParent - units of this dimension included with
 *   each unit of the parent (three registers per location); null when nothing is included.
 * @property {string} priceEnding - what the dimension's monthly unit price must end in (`PRICE_ENDING`).
 */

/**
 * @typedef {Object} AppConsumable
 * @property {string} consumableKey - one of `CONSUMABLE_KEY_VALUES`.
 * @property {string} scope - `account` | `per_parent`.
 * @property {?string} parentDimensionKey - the dimension each inclusion is counted per (required when `per_parent`).
 * @property {number} includedQuantity - included per scope unit per period.
 * @property {boolean} pooled - whether per-parent inclusions pool across the account's parent units.
 * @property {number} blockSize - the whole block sold beyond the inclusion, under a store-set cap.
 */

/**
 * @typedef {Object} AppCatalogEntry
 * @property {string} appKey
 * @property {string} kind - `anchor` | `app`.
 * @property {string} label - the product name.
 * @property {?string[]} attachesTo - anchor keys an app attaches to; null for an anchor.
 * @property {string[]} capabilityKeys - the `@puffin-core/tenant-capabilities` keys holding it grants.
 * @property {string} pricingModel
 * @property {?number} shareBps - the recovery share, for `share_of_recovery` only.
 * @property {AppDimension[]} dimensions - at most two.
 * @property {AppConsumable[]} consumables
 * @property {boolean} isSellable - false while a product is not offered (ProPuffin).
 * @property {boolean} comingSoon - shown deactivated with a "coming soon" note in the anchor question.
 * @property {?string} launchAppKey - the cross-app-policy app that opens this row, or null.
 * @property {boolean} switchable - an INCLUDED app the account may turn off and on (owner ruling Q-LS-85: customer
 *   accounts are "some kind of account attribute or setting" — on with every plan, off per company); every other
 *   included app follows its anchor.
 * @property {number} sortOrder
 */

const ANCHOR_KEYS_ALL = ['parcel', 'publish', 'pro'];

function dimension(
  dimensionKey,
  quantitySource,
  includedPerParent = null,
  priceEnding = PRICE_ENDING.NINETY_FIVE,
) {
  return { dimensionKey, quantitySource, includedPerParent, priceEnding };
}

function consumable({
  consumableKey,
  scope,
  parentDimensionKey = null,
  includedQuantity,
  pooled = false,
  blockSize,
}) {
  return { consumableKey, scope, parentDimensionKey, includedQuantity, pooled, blockSize };
}

/** The raw catalog list — the ONE place app keys are defined. Order is display order. */
const CATALOG_LIST = [
  {
    appKey: 'parcel',
    kind: APP_KIND.ANCHOR,
    label: 'ParcelPuffin',
    attachesTo: null,
    // Owner ruling Q-AE-47 (2026-10-15): analytics, the owner console and gated pages are features of EVERY anchor's
    // hosted website, beside the host set. The quote-request form is NOT among them: it is the switchable free app
    // `quotes` below (owner ruling Q-AE-68, 2026-10-17).
    capabilityKeys: ['pos', 'website', 'hosting', 'analytics', 'owner-console', 'page-access'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [
      dimension(DIMENSION_KEY.LOCATION, QUANTITY_SOURCE.DERIVED),
      dimension(DIMENSION_KEY.REGISTER, QUANTITY_SOURCE.DERIVED, {
        dimensionKey: DIMENSION_KEY.LOCATION,
        quantity: 3,
      }),
    ],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'parcel',
    switchable: false,
  },
  {
    appKey: 'publish',
    kind: APP_KIND.ANCHOR,
    label: 'PublishPuffin',
    attachesTo: null,
    capabilityKeys: ['website', 'hosting', 'blog', 'owner-console', 'analytics', 'page-access'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [dimension(DIMENSION_KEY.SITE, QUANTITY_SOURCE.DERIVED)],
    consumables: [
      consumable({
        consumableKey: CONSUMABLE_KEY.BLOG_POSTS,
        scope: CONSUMABLE_SCOPE.PER_PARENT,
        parentDimensionKey: DIMENSION_KEY.SITE,
        includedQuantity: 4,
        blockSize: 4,
      }),
    ],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'purchase',
    switchable: false,
  },
  {
    appKey: 'pro',
    kind: APP_KIND.ANCHOR,
    label: 'ProPuffin',
    attachesTo: null,
    capabilityKeys: [
      'field-service',
      'website',
      'hosting',
      'analytics',
      'owner-console',
      'page-access',
    ],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [
      dimension(DIMENSION_KEY.OFFICE, QUANTITY_SOURCE.DERIVED),
      dimension(DIMENSION_KEY.TECHNICIAN, QUANTITY_SOURCE.DERIVED),
    ],
    consumables: [],
    isSellable: false,
    comingSoon: true,
    launchAppKey: null,
    switchable: false,
  },
  {
    appKey: 'palm',
    kind: APP_KIND.APP,
    label: 'PalmPuffin',
    attachesTo: ANCHOR_KEYS_ALL,
    capabilityKeys: ['team-app'],
    pricingModel: PRICING_MODEL.INCLUDED_WITH_ANCHOR,
    shareBps: null,
    dimensions: [],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'palm',
    switchable: false,
  },
  {
    appKey: 'prep',
    kind: APP_KIND.APP,
    label: 'PrepPuffin',
    attachesTo: ANCHOR_KEYS_ALL,
    capabilityKeys: ['training'],
    pricingModel: PRICING_MODEL.INCLUDED_WITH_ANCHOR,
    shareBps: null,
    dimensions: [],
    consumables: [
      consumable({
        consumableKey: CONSUMABLE_KEY.AI_GENERATIONS,
        scope: CONSUMABLE_SCOPE.ACCOUNT,
        includedQuantity: 10,
        blockSize: 10,
      }),
    ],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'prep',
    switchable: false,
  },
  {
    // Owner ruling Q-LS-85 / Q-LS-86 / Q-LS-87 (2026-10-11): customer accounts are a per-company SETTING — on with
    // every plan when the company is created, switched off per company (the content sites), refused off while the
    // company has registered storefront customers (`CUSTOMER_ACCOUNTS_IN_USE`, the registry's removal guard), and the
    // storefront needs it (`ecommerce` depends on `customer-account`). In this catalog a setting an account holds is
    // an app it holds: INCLUDED with the anchor and SWITCHABLE, the one row of its kind.
    appKey: 'accounts',
    kind: APP_KIND.APP,
    label: 'Customer accounts',
    attachesTo: ['parcel', 'publish'],
    capabilityKeys: ['customer-account'],
    pricingModel: PRICING_MODEL.INCLUDED_WITH_ANCHOR,
    shareBps: null,
    dimensions: [],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: null,
    switchable: true,
  },
  {
    // DTOWNRETAIL-SITE-001 V3 (R-41): the quote-request form WITHOUT the catalog, the cart and the checkout — it
    // needs a site to live on, not a storefront, and a quote converts into an order, so `print-quotes` serves quotes
    // and orders. Owner ruling Q-AE-68 (2026-10-17, "Free, switchable"): in this catalog it is the second switchable
    // free app — included with every anchor, a company's order names it or not (D Town's does, the content sites' do
    // not), and a new store chooses at creation beside customer accounts. A site switched off mounts no quote form.
    appKey: 'quotes',
    kind: APP_KIND.APP,
    label: 'Quote requests',
    attachesTo: ANCHOR_KEYS_ALL,
    capabilityKeys: ['print-quotes'],
    pricingModel: PRICING_MODEL.INCLUDED_WITH_ANCHOR,
    shareBps: null,
    dimensions: [],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'purchase',
    switchable: true,
  },
  {
    appKey: 'planner',
    kind: APP_KIND.APP,
    label: 'PlannerPuffin',
    attachesTo: ['parcel', 'pro'],
    capabilityKeys: ['scheduling'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [dimension(DIMENSION_KEY.LOCATION, QUANTITY_SOURCE.DERIVED)],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'planner',
    switchable: false,
  },
  {
    appKey: 'payday',
    kind: APP_KIND.APP,
    label: 'PayDayPuffin',
    attachesTo: ['parcel', 'pro'],
    capabilityKeys: ['payroll'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    // Q-AE-14: "$49.95 + $6 per employee paid" — a flat base on the account, and whole dollars per employee.
    dimensions: [
      dimension(DIMENSION_KEY.ACCOUNT, QUANTITY_SOURCE.DERIVED),
      dimension(
        DIMENSION_KEY.EMPLOYEE_PAID,
        QUANTITY_SOURCE.DERIVED,
        null,
        PRICE_ENDING.WHOLE_DOLLARS,
      ),
    ],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'payday',
    switchable: false,
  },
  {
    appKey: 'port',
    kind: APP_KIND.APP,
    label: 'PortPuffin',
    attachesTo: ANCHOR_KEYS_ALL,
    capabilityKeys: ['phone'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [
      dimension(DIMENSION_KEY.LINE, QUANTITY_SOURCE.DERIVED),
      dimension(DIMENSION_KEY.AI_RECEPTIONIST, QUANTITY_SOURCE.DECLARED),
    ],
    consumables: [
      consumable({
        consumableKey: CONSUMABLE_KEY.SMS_SEGMENTS,
        scope: CONSUMABLE_SCOPE.PER_PARENT,
        parentDimensionKey: DIMENSION_KEY.LINE,
        includedQuantity: 250,
        pooled: true,
        blockSize: 500,
      }),
      consumable({
        consumableKey: CONSUMABLE_KEY.AI_MINUTES,
        scope: CONSUMABLE_SCOPE.PER_PARENT,
        parentDimensionKey: DIMENSION_KEY.AI_RECEPTIONIST,
        includedQuantity: 150,
        blockSize: 100,
      }),
      // Owner rulings Q-AE-57 / Q-AE-58 (2026-10-15, OUTCOME-SKU-CONSUMABLE-001): outcomes as a priced consumable ON
      // TOP of minutes — 25 real AI outcomes included per AI receptionist, blocks of 25 under a store-set cap; the
      // block PRICE is the console's (G-MONEY). Metered by port-api's outcome ledger, judged by the ONE consumption
      // gate before a billable skill runs, invoiced at the ONE period close.
      consumable({
        consumableKey: CONSUMABLE_KEY.AI_OUTCOMES,
        scope: CONSUMABLE_SCOPE.PER_PARENT,
        parentDimensionKey: DIMENSION_KEY.AI_RECEPTIONIST,
        includedQuantity: 25,
        blockSize: 25,
      }),
    ],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'port',
    switchable: false,
  },
  {
    appKey: 'pronto',
    kind: APP_KIND.APP,
    label: 'ProntoPuffin',
    attachesTo: ANCHOR_KEYS_ALL,
    capabilityKeys: ['email'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [dimension(DIMENSION_KEY.MAILBOX, QUANTITY_SOURCE.DERIVED)],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: null,
    switchable: false,
  },
  {
    appKey: 'patrol',
    kind: APP_KIND.APP,
    label: 'PatrolPuffin',
    attachesTo: ['parcel'],
    capabilityKeys: ['carrier-audit'],
    pricingModel: PRICING_MODEL.SHARE_OF_RECOVERY,
    shareBps: PATROL_RECOVERY_SHARE_BPS,
    dimensions: [],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'patrol',
    switchable: false,
  },
  {
    appKey: 'storefront',
    kind: APP_KIND.APP,
    label: 'PurchasePuffin storefront',
    attachesTo: ANCHOR_KEYS_ALL,
    capabilityKeys: ['ecommerce'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [dimension(DIMENSION_KEY.STOREFRONT, QUANTITY_SOURCE.DERIVED)],
    consumables: [],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'purchase',
    switchable: false,
  },
  {
    // Owner ruling Q-AE-46 (2026-10-15): the Auto-blog is a MENU item for the anchors whose site does not carry it —
    // a ParcelPuffin or ProPuffin store adds it and pays per block of four posts under a store-set cap, nothing
    // included, no monthly base; PublishPuffin's own site includes four posts a month (Q-AE-28), so it never attaches
    // there. Managed where the tenant site is (the purchase surfaces).
    appKey: 'blog',
    kind: APP_KIND.APP,
    label: 'Auto-blog',
    attachesTo: ['parcel', 'pro'],
    capabilityKeys: ['blog'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [],
    consumables: [
      consumable({
        consumableKey: CONSUMABLE_KEY.BLOG_POSTS,
        scope: CONSUMABLE_SCOPE.ACCOUNT,
        includedQuantity: 0,
        blockSize: 4,
      }),
    ],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'purchase',
    switchable: false,
  },
  {
    appKey: 'prospect',
    kind: APP_KIND.APP,
    label: 'ProspectPuffin',
    attachesTo: ANCHOR_KEYS_ALL,
    capabilityKeys: ['business-development'],
    pricingModel: PRICING_MODEL.PER_UNIT,
    shareBps: null,
    dimensions: [dimension(DIMENSION_KEY.LOCATION, QUANTITY_SOURCE.DERIVED)],
    consumables: [
      consumable({
        consumableKey: CONSUMABLE_KEY.AI_QUALIFICATIONS,
        scope: CONSUMABLE_SCOPE.ACCOUNT,
        includedQuantity: 200,
        blockSize: 100,
      }),
    ],
    isSellable: true,
    comingSoon: false,
    launchAppKey: 'prospect',
    switchable: false,
  },
];

function deepFreezeEntry(entry, index) {
  const dimensions = Object.freeze(
    entry.dimensions.map((d) =>
      Object.freeze({
        ...d,
        includedPerParent: d.includedPerParent ? Object.freeze({ ...d.includedPerParent }) : null,
      }),
    ),
  );
  const consumables = Object.freeze(entry.consumables.map((c) => Object.freeze({ ...c })));
  return Object.freeze({
    ...entry,
    attachesTo: entry.attachesTo ? Object.freeze([...entry.attachesTo]) : null,
    capabilityKeys: Object.freeze([...entry.capabilityKeys]),
    dimensions,
    consumables,
    sortOrder: index + 1,
  });
}

/** The frozen catalog, in display order. */
const APP_CATALOG = Object.freeze(CATALOG_LIST.map(deepFreezeEntry));

/** Frozen null-prototype lookup by app key, so `BY_KEY['constructor']` misses. */
const APP_CATALOG_BY_KEY = Object.freeze(
  APP_CATALOG.reduce((acc, entry) => {
    acc[entry.appKey] = entry;
    return acc;
  }, Object.create(null)),
);

/** Every app key, in display order. */
const APP_KEYS = Object.freeze(APP_CATALOG.map((entry) => entry.appKey));

/** The anchor keys, in display order. */
const ANCHOR_APP_KEYS = Object.freeze(
  APP_CATALOG.filter((entry) => entry.kind === APP_KIND.ANCHOR).map((entry) => entry.appKey),
);

/**
 * The anchor the SHARED web host keys (`website`, `hosting`) name on their own: PublishPuffin is the web-presence
 * anchor; beside ParcelPuffin the same keys are ParcelPuffin's included features (`appsFromCapabilities`).
 */
const WEB_ANCHOR_APP_KEY = 'publish';

/** The apps free with any anchor, in display order. */
const INCLUDED_APP_KEYS = Object.freeze(
  APP_CATALOG.filter((entry) => entry.pricingModel === PRICING_MODEL.INCLUDED_WITH_ANCHOR).map(
    (entry) => entry.appKey,
  ),
);

/**
 * @param {unknown} key
 * @returns {boolean} true when `key` is a catalog app key.
 */
function isAppKey(key) {
  return typeof key === 'string' && Object.hasOwn(APP_CATALOG_BY_KEY, key);
}

/**
 * @param {string} appKey
 * @returns {?AppCatalogEntry} the entry, or null for a key the catalog does not hold.
 */
function getApp(appKey) {
  return isAppKey(appKey) ? APP_CATALOG_BY_KEY[appKey] : null;
}

/**
 * @param {unknown} key
 * @returns {boolean} true when `key` names an anchor.
 */
function isAnchorKey(key) {
  const entry = getApp(key);
  return entry !== null && entry.kind === APP_KIND.ANCHOR;
}

/**
 * Whether `appKey` may be held beside the anchor `anchorKey`: an anchor attaches to nothing (it IS the anchor);
 * an app attaches to the anchors it lists.
 *
 * @param {string} appKey
 * @param {string} anchorKey
 * @returns {boolean}
 */
function attachesTo(appKey, anchorKey) {
  const entry = getApp(appKey);
  if (entry === null || entry.kind === APP_KIND.ANCHOR) return false;
  return entry.attachesTo.includes(anchorKey);
}

/**
 * The apps that attach to `anchorKey`, in display order (the basket's and the switcher's roster).
 *
 * @param {string} anchorKey
 * @returns {AppCatalogEntry[]} empty for a key that is not an anchor.
 */
function appsAttachedTo(anchorKey) {
  if (!isAnchorKey(anchorKey)) return [];
  return APP_CATALOG.filter((entry) => attachesTo(entry.appKey, anchorKey));
}

/**
 * The catalog apps that OPEN a cross-app-policy surface (`launchAppKey`), in display order: a launcher tile, or a
 * cross-app hop, is admitted when the company holds ANY of them (`publish` and `storefront` both open `purchase`).
 *
 * @param {string} launchAppKey - A cross-app-policy app key.
 * @returns {AppCatalogEntry[]} empty for a surface no catalog app opens.
 */
function appsThatLaunch(launchAppKey) {
  if (typeof launchAppKey !== 'string') return [];
  return APP_CATALOG.filter((entry) => entry.launchAppKey === launchAppKey);
}

module.exports = {
  MAX_DIMENSIONS_PER_APP,
  PATROL_RECOVERY_SHARE_BPS,
  APP_CATALOG,
  APP_CATALOG_BY_KEY,
  APP_KEYS,
  ANCHOR_APP_KEYS,
  WEB_ANCHOR_APP_KEY,
  INCLUDED_APP_KEYS,
  isAppKey,
  getApp,
  isAnchorKey,
  attachesTo,
  appsAttachedTo,
  appsThatLaunch,
};
